'use client'

import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { RevealText } from './reveal-text'

const highlights = [
  { title: 'Small Business Websites', desc: 'Fast, credible sites that convert visitors into leads.' },
  { title: 'E-commerce & Booking', desc: 'Online sales and scheduling built for your workflow.' },
  { title: 'Custom Software', desc: 'POS, CRM, invoicing, and internal tools your team uses daily.' },
]

const values = [
  {
    number: '01',
    title: 'Ship, don\'t slide',
    description:
      'We optimize for launches you can point to - live URLs, logged-in users, and workflows your staff runs every day.',
  },
  {
    number: '02',
    title: 'One partner',
    description:
      'Strategy, design, and engineering in one studio - no handoffs between agencies and dev shops.',
  },
  {
    number: '03',
    title: 'Founder-friendly',
    description:
      'Plain language, weekly checkpoints, and scope you can understand before we write code.',
  },
  {
    number: '04',
    title: 'Built to last',
    description:
      'Maintainable stacks, documentation, and support options when you want us after go-live.',
  },
]

export function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const valuesRef = useRef<HTMLDivElement>(null)
  const isValuesInView = useInView(valuesRef, { once: true, margin: '-100px' })

  return (
    <section ref={sectionRef} id="about" className="py-32 md:py-48 px-6 lg:px-12 bg-card/50">
      <div className="max-w-7xl mx-auto">
        <motion.div className="grid md:grid-cols-12 gap-12 lg:gap-20 mb-24 md:mb-32">
          <div className="md:col-span-5">
            <RevealText className="mb-4">
              <span className="text-xs tracking-[0.3em] uppercase text-accent">About</span>
            </RevealText>
            <RevealText delay={0.1}>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight">
                Elementary Creative
              </h2>
            </RevealText>
          </div>
          <div className="md:col-span-7">
            <RevealText delay={0.2}>
              <p className="text-xl md:text-2xl text-muted-foreground font-serif italic leading-relaxed mb-8">
                We&apos;re a digital product studio helping businesses launch websites and custom
                software - from marketing sites to POS, booking, and CRM tools.
              </p>
            </RevealText>
            <RevealText delay={0.3}>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Elementary Solutions started with a simple idea: small teams deserve the same quality
                of design and engineering as enterprise product orgs, without the overhead. We work
                closely with owners and operators who need tools that perform in production.
              </p>
            </RevealText>
            <ul className="space-y-4">
              {highlights.map((item) => (
                <li key={item.title} className="border-l-2 border-accent pl-4">
                  <p className="font-medium">{item.title}</p>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <div ref={valuesRef}>
          <RevealText className="mb-12">
            <span className="text-xs tracking-[0.3em] uppercase text-accent">How we work</span>
          </RevealText>
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {values.map((value, index) => (
              <motion.div
                key={value.number}
                className="p-8 border border-border bg-background"
                initial={{ opacity: 0, y: 30 }}
                animate={isValuesInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <span className="text-sm font-mono text-accent mb-4 block">{value.number}</span>
                <h3 className="text-2xl font-serif mb-4">{value.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
